import { Injectable } from "@angular/core";
import { StorageService } from "./storage.service";
import { Features, FeatureStatus, IFeatureFlag } from "../features.enums";
import { SignalRService } from "./signalr.service";
import { HttpClient } from "@angular/common/http";
import { FeatureFlagAccess } from "../models/feature-flag-access.model";
import { environment } from "src/environments/environment";
import { Observable, retry, share, Subject, Subscription, switchMap, takeUntil, timer } from "rxjs";

@Injectable({
	providedIn: 'root',
})
export class FeatureFlagService {
	private stopPolling = new Subject();
	private subscription: Subscription;

	constructor(private httpClient: HttpClient, private signalRService: SignalRService) { }

	ngOnDestroy() {
		this.stopPolling.next(true);
		this.subscription.unsubscribe();
	}

	async connectToHub(): Promise<void> {
		try {
			await this.signalRService.startConnection();
			this.signalRService.addFeatureFlagDataListener((featureFlagAccess) => {
				this.updateFeatureFlags(featureFlagAccess);
			});
		} catch(error) {
			console.error("Failed to start SignalR connection:", error);
			throw new Error();
		}
	}

	async queryFeatureFlag(featureFlagKey: string): Promise<void> {
        try {
            await this.connectToHub();
            await this.getFeatureFlagFromHub(featureFlagKey);
        } catch(error) {
            this.getFeatureFlagFromApi(featureFlagKey);
        }
    }

    async getFeatureFlagFromHub(featureFlagKey: string): Promise<void> {
		try {
			await this.signalRService.receiveMessage(
				StorageService.activeTenant.tenantId,
				featureFlagKey
			);
		} catch (error) {
			console.error("Failed to get feature flag from Hub:", error);
		}
	}

    getFeatureFlagFromApi(featureFlagKey: string): void {
        try {
			this.sendFeatureFlagRequest(featureFlagKey).subscribe((res: FeatureFlagAccess) => {
                this.updateFeatureFlags(res);
            });
		} catch (error) {
			console.error("Failed to get feature flag from API:", error);
		}
    }

	sendFeatureFlagRequest(featureFlagKey: string): Observable<FeatureFlagAccess> {
		const url = `${environment.apiUrl}/tenants/${StorageService.activeTenant.tenantId}/${featureFlagKey}`;
		return this.httpClient.get<FeatureFlagAccess>(url);
	}

	pollApi(featureFlagKey: string): Observable<FeatureFlagAccess> {
		return timer(1, 30000).pipe(
			switchMap(() => this.sendFeatureFlagRequest(featureFlagKey)), 
			retry(), 
			share(),
			takeUntil(this.stopPolling)
		);
	}

	updateFeatureFlags(featureFlagAccess: FeatureFlagAccess): void {
        StorageService.featureFlagAccess = featureFlagAccess;
		StorageService.featureFlagGuardTrigger = featureFlagAccess;
	}


	// Old feature flag code - to be replaced
	static get features(): IFeatureFlag {
		return StorageService.features;
	}

	static get featureOverrides(): IFeatureFlag {
		return StorageService.configs.featureOverrides;
	}

	static isStable(feature: Features): boolean {
		return (
			!FeatureFlagService.isOff(feature) &&
			FeatureFlagService.features[feature] === FeatureStatus.Stable
		);
	}

	static isLatest(feature: Features): boolean {
		return (
			!FeatureFlagService.isOff(feature) &&
			FeatureFlagService.features[feature] === FeatureStatus.Latest
		);
	}

	static isOff(feature: Features): boolean {
		return FeatureFlagService.features[feature] === FeatureStatus.Off;
	}
}
